import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { TemplateCard } from "@/components/TemplateCard";
import { getAllTemplates, getTemplateById, type ResumeTemplate } from "@/lib/resumeTemplates";
import { ArrowRight, FileCheck, Sparkles } from "lucide-react";
import { AutoTranslate } from "@/lib/autoTranslate.tsx";

interface TemplateGalleryProps {
  onSelectTemplate: (templateId: string) => void;
  showKeepOriginal?: boolean;
  recommendedTemplateId?: string;
  recommendationReason?: string;
}

export function TemplateGallery({
  onSelectTemplate,
  showKeepOriginal = false,
  recommendedTemplateId,
  recommendationReason,
}: TemplateGalleryProps) {
  const templates = getAllTemplates(showKeepOriginal);

  // Pre-select the recommended template so the confirm button is enabled
  // immediately. The user can still pick any other template.
  const [selectedTemplate, setSelectedTemplate] = useState<ResumeTemplate | null>(
    () => (recommendedTemplateId ? getTemplateById(recommendedTemplateId) ?? null : null)
  );

  useEffect(() => {
    if (recommendedTemplateId && !selectedTemplate) {
      const tpl = getTemplateById(recommendedTemplateId);
      if (tpl) setSelectedTemplate(tpl);
    }
  }, [recommendedTemplateId, selectedTemplate]);

  const handleTemplateSelect = (template: ResumeTemplate) => {
    setSelectedTemplate(template);
  };

  const handleConfirm = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate.id);
    }
  };

  return (
    <div className="bg-card/80 backdrop-blur-sm rounded-2xl border border-card-border p-6 shadow-lg">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-3">
          <FileCheck className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">
          {AutoTranslate(`Choose Your Template`)}
        </h2>
        <p className="text-sm text-muted-foreground">
          {AutoTranslate(`Select a professional template for your resume`)}
        </p>

        {recommendedTemplateId && recommendationReason && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary font-medium bg-primary/10 px-3 py-1.5 rounded-full">
            <Sparkles className="w-3.5 h-3.5" />
            {recommendationReason}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplate?.id === template.id}
            isRecommended={template.id === recommendedTemplateId}
            recommendationReason={
              template.id === recommendedTemplateId ? recommendationReason : undefined
            }
            onSelect={handleTemplateSelect}
          />
        ))}
      </div>

      <div className="flex justify-center">
        <Button
          onClick={handleConfirm}
          disabled={!selectedTemplate}
          size="lg"
          className="min-w-[200px]"
        >
          {AutoTranslate(`Use This Template`)}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
